import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { studentsAPI, usersAPI, chatAPI, messageTemplatesAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import {
  BUILT_IN_TEMPLATES,
  TEMPLATE_CATEGORIES,
  adaptCustomTemplate,
  renderTemplateBody,
} from '../utils/messageTemplates';

/*
 * Internal Communication & Chat System
 *
 * Sidebar — populated from real school data:
 *   All Students    → students table
 *   All Teachers    → school users (real DM targets)
 *   Parents         → derived from students (father_name + mobile_number)
 *   Specific Class  → real class list from /students/meta/classes
 *   Individual Stud → real students, searchable
 *   Transport / Accounts → blank until those modules exist
 *
 * Messages — persisted in Azure SQL (chat_messages table) via /api/chat/*.
 *   DM:        thread_key = dm:<sortedUsernames>
 *   Broadcast: thread_key = bc:students | bc:teachers | bc:parents
 *   Class:     thread_key = cls:<class> or cls:<class>:parents
 *   Student:   thread_key = stu:<id> or stu:<id>:parent
 * The active thread polls every 3 s for new messages.
 */

const CATEGORIES = [
  { value: 'all',        label: 'All Conversations' },
  { value: 'students',   label: 'All Students' },
  { value: 'teachers',   label: 'All Teachers' },
  { value: 'parents',    label: 'Parents' },
  { value: 'transport',  label: 'Transport Staff' },
  { value: 'class',      label: 'Specific Class' },
  { value: 'individual', label: 'Individual Student' },
  { value: 'accounts',   label: 'Accounts Department' },
];

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

// Decode the custom JWT payload (base64url(payload).signature) to get
// the current user's username. Returns '' if decoding fails.
const decodeMyUsername = () => {
  try {
    const tok = sessionStorage.getItem('gs_token');
    if (!tok) return '';
    const payloadB64 = tok.split('.')[0];
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const obj = JSON.parse(json);
    return obj.user || '';
  } catch {
    return '';
  }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// ── WhatsApp-like additions (purely additive, no DB changes) ────────────────
// Encode optional metadata (reply quote, attachment) inside the message body
// using non-printing markers so plain text messages keep working unchanged.
const META_PFX = '\u0002';
const META_SFX = '\u0003\n';
const encodePayload = (meta, body) => {
  if (!meta || (!meta.reply && !meta.file)) return body || '';
  return META_PFX + JSON.stringify(meta) + META_SFX + (body || '');
};
const decodePayload = (content) => {
  if (!content || content[0] !== META_PFX) return { meta: null, body: content || '' };
  const end = content.indexOf(META_SFX);
  if (end < 0) return { meta: null, body: content };
  try {
    return { meta: JSON.parse(content.slice(1, end)), body: content.slice(end + META_SFX.length) };
  } catch {
    return { meta: null, body: content };
  }
};

const EMOJIS = [
  '\u{1F600}','\u{1F602}','\u{1F60A}','\u{1F60D}','\u{1F618}','\u{1F914}','\u{1F644}','\u{1F60E}',
  '\u{1F62D}','\u{1F621}','\u{1F44D}','\u{1F44E}','\u{1F44F}','\u{1F64F}','\u{1F4AF}','\u{1F525}',
  '\u2764\uFE0F','\u{1F389}','\u{1F38A}','\u2705','\u274C','\u26A0\uFE0F','\u{1F4DA}','\u{1F393}',
  '\u{1F4DD}','\u{1F4C5}','\u{1F4CC}','\u{1F4F7}','\u{1F4F1}','\u{1F4E2}','\u{1F517}','\u{1F319}',
];

const sameDay = (a, b) => a.toDateString() === b.toDateString();
const fmtDateSep = (iso) => {
  try {
    const d  = new Date(iso);
    const t  = new Date();
    const y  = new Date(); y.setDate(y.getDate() - 1);
    if (sameDay(d, t)) return 'Today';
    if (sameDay(d, y)) return 'Yesterday';
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

// One-shot Web Audio beep for incoming messages (no asset files needed).
let _audioCtx = null;
const playBeep = () => {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.26);
  } catch { /* ignore */ }
};

const PIN_KEY = 'gs_chat_pins';
const loadPins = () => {
  try { return JSON.parse(localStorage.getItem(PIN_KEY) || '[]'); } catch { return []; }
};
const savePins = (arr) => { try { localStorage.setItem(PIN_KEY, JSON.stringify(arr)); } catch { /* ignore */ } };

const MAX_FILE_BYTES = 500 * 1024; // 500 KB cap so messages stay reasonably small
const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload  = () => resolve(fr.result);
  fr.onerror = () => reject(fr.error);
  fr.readAsDataURL(file);
});

const styles = {
  shell:      { display: 'flex', height: 'calc(100vh - 160px)', minHeight: 480, background: '#f0f2f5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
  sidebar:    { width: 340, minWidth: 280, display: 'flex', flexDirection: 'column', background: '#ffffff', borderRight: '1px solid #e5e7eb' },
  sbHeader:   { padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#075e54', color: '#fff' },
  sbTitle:    { fontSize: 16, fontWeight: 600 },
  newBtn:     { background: '#25d366', color: '#fff', border: 'none', borderRadius: 20, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  sbToolbar:  { padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 },
  search:     { width: '100%', padding: '8px 12px', borderRadius: 20, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' },
  select:     { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  list:       { flex: 1, overflowY: 'auto' },
  item:       { display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 12, cursor: 'pointer', borderBottom: '1px solid #f1f5f9' },
  itemActive: { background: '#e7f3ff' },
  avatarWrap: { position: 'relative', flexShrink: 0 },
  avatar:     { width: 44, height: 44, borderRadius: '50%', background: '#075e54', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 },
  itemMain:   { flex: 1, minWidth: 0 },
  itemTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  itemName:   { fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemBot:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 },
  itemPrev:   { fontSize: 12.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 },
  empty:      { padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 },
  catNote:    { padding: '8px 14px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 11.5, color: '#92400e' },
  loading:    { padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 },

  chatPane:   { flex: 1, display: 'flex', flexDirection: 'column', background: '#efeae2' },
  chatHead:   { padding: '12px 18px', background: '#f0f2f5', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 },
  chatTitle:  { fontSize: 15, fontWeight: 600, color: '#111827' },
  chatSub:    { fontSize: 12, color: '#6b7280' },
  msgArea:    { flex: 1, overflowY: 'auto', padding: '18px 24px', background: 'repeating-linear-gradient(45deg, #efeae2, #efeae2 12px, #e9e4dc 12px, #e9e4dc 24px)', display: 'flex', flexDirection: 'column', gap: 8 },
  bubbleRow:  { display: 'flex', width: '100%' },
  bubble:     { maxWidth: '70%', padding: '8px 12px', borderRadius: 8, fontSize: 13.5, lineHeight: 1.35, color: '#111827', boxShadow: '0 1px 1px rgba(0,0,0,0.05)', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
  bubbleMine: { background: '#d9fdd3' },
  bubbleThem: { background: '#ffffff' },
  bubbleMeta: { fontSize: 10.5, color: '#6b7280', marginTop: 4, textAlign: 'right' },
  bubbleAuthor:{ fontSize: 11, fontWeight: 600, color: '#075e54', marginBottom: 2 },
  composer:   { display: 'flex', gap: 10, padding: 12, background: '#f0f2f5', borderTop: '1px solid #e5e7eb' },
  input:      { flex: 1, padding: '10px 14px', borderRadius: 22, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', background: '#fff' },
  sendBtn:    { background: '#075e54', color: '#fff', border: 'none', borderRadius: '50%', width: 42, height: 42, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  placeholder:{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#6b7280', textAlign: 'center', padding: 20 },
  phIcon:     { fontSize: 60, marginBottom: 12, opacity: 0.6 },
  err:        { padding: '6px 12px', background: '#fee2e2', color: '#991b1b', fontSize: 12, borderBottom: '1px solid #fecaca' },
};

function Avatar({ initials, color = '#075e54' }) {
  return (
    <div style={styles.avatarWrap}>
      <div style={{ ...styles.avatar, background: color }}>{initials}</div>
    </div>
  );
}

function ConversationItem({ convo, active, onClick, onPin }) {
  return (
    <div
      style={active ? { ...styles.item, ...styles.itemActive } : styles.item}
      onClick={onClick}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f9fafb'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Avatar initials={convo.avatar} />
      <div style={styles.itemMain}>
        <div style={styles.itemTop}>
          <span style={styles.itemName}>
            {convo._pinned && <span title="Pinned" style={{ marginRight: 4 }}>📌</span>}
            {convo.name}
          </span>
          {convo.unread > 0 && (
            <span style={{
              background: '#25d366', color: '#fff', borderRadius: 12,
              minWidth: 22, height: 20, padding: '0 7px', fontSize: 11,
              fontWeight: 700, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', marginLeft: 8,
            }}>
              {convo.unread > 99 ? '99+' : convo.unread}
            </span>
          )}
        </div>
        <div style={styles.itemBot}>
          <span style={{
            ...styles.itemPrev,
            fontWeight: convo.unread > 0 ? 600 : 400,
            color:      convo.unread > 0 ? '#111827' : '#6b7280',
          }}>{convo.subtitle || ''}</span>
          {convo._tk && onPin && (
            <button
              onClick={(e) => { e.stopPropagation(); onPin(convo._tk); }}
              title={convo._pinned ? 'Unpin' : 'Pin to top'}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 14, padding: '0 4px', marginLeft: 6,
                opacity: convo._pinned ? 1 : 0.4,
              }}
            >📌</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Map a sidebar item to a backend thread_key + display title.
// Returns null when the recipient isn't messageable from this account.
function resolveThread(item, meUsername) {
  if (!item) return null;
  const { id, name } = item;

  if (id === 'broadcast-students') return { thread_key: 'bc:students', thread_title: 'All Students Broadcast' };
  if (id === 'broadcast-teachers') return { thread_key: 'bc:teachers', thread_title: 'All Teachers' };
  if (id === 'broadcast-parents')  return { thread_key: 'bc:parents',  thread_title: 'All Parents Broadcast' };

  if (id.startsWith('cls-') && id.endsWith('-grp')) {
    const cls = id.slice(4, -4);
    return { thread_key: `cls:${cls}`, thread_title: `Class ${cls}` };
  }
  if (id.startsWith('cls-') && id.endsWith('-par')) {
    const cls = id.slice(4, -4);
    return { thread_key: `cls:${cls}:parents`, thread_title: `Parents of Class ${cls}` };
  }
  if (id.startsWith('stu-')) return { thread_key: `stu:${id.slice(4)}`,        thread_title: name };
  if (id.startsWith('par-')) return { thread_key: `stu:${id.slice(4)}:parent`, thread_title: name };

  if (id.startsWith('usr-')) {
    const target = (item.username || '').toLowerCase();
    const me = (meUsername || '').toLowerCase();
    if (!target || !me || target === me) return null;
    const sorted = [me, target].sort();
    return { thread_key: `dm:${sorted.join('|')}`, thread_title: name };
  }
  return null;
}

export default function Chat() {
  const { year } = useYear();

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState('');

  const [activeId,    setActiveId]    = useState(null);
  const [search,      setSearch]      = useState('');
  const [category,    setCategory]    = useState('all');
  const [pickedClass, setPickedClass] = useState('');
  const [draft,       setDraft]       = useState('');

  const [messages, setMessages] = useState([]);
  const [msgErr,   setMsgErr]   = useState('');
  const [sending,  setSending]  = useState(false);
  const [waSending, setWaSending] = useState(false);

  // WhatsApp-style extras
  const [replyTo,     setReplyTo]     = useState(null);   // { id, author, text }
  const [attachment,  setAttachment]  = useState(null);   // { name, type, data }
  const [showEmoji,   setShowEmoji]   = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [pins,        setPins]        = useState(loadPins());
  const [otherSeenId, setOtherSeenId] = useState(0);      // DM only
  const [convoSearch, setConvoSearch] = useState('');     // search within open chat
  const fileInputRef     = useRef(null);
  const draftInputRef    = useRef(null);
  const readStatusPollRef = useRef(null);
  const firstLoadRef     = useRef(true);

  // Map of thread_key → { last_message, last_message_id, created_at, unread_count, thread_title }
  // Populated by polling /api/chat/threads. Drives unread badges + recency sort.
  const [threadIdx, setThreadIdx] = useState({});

  const meUsername = useMemo(decodeMyUsername, []);
  const scrollRef  = useRef(null);
  const pollRef    = useRef(null);
  const threadsPollRef = useRef(null);
  const lastIdRef  = useRef(0);

  // Poll the threads list (for unread badges + last-message previews + sort)
  const refreshThreads = useCallback(async () => {
    try {
      const res = await chatAPI.listThreads();
      const list = res.data?.data || [];
      const idx = {};
      for (const t of list) {
        idx[t.thread_key] = {
          last_message:    t.last_message,
          last_message_id: t.last_message_id,
          created_at:      t.created_at,
          unread_count:    t.unread_count || 0,
          thread_title:    t.thread_title,
          sender_name:     t.sender_name,
        };
      }
      // Only update state when something actually changed to avoid unnecessary re-renders
      setThreadIdx((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(idx);
        if (prevKeys.length !== nextKeys.length) return idx;
        for (const k of nextKeys) {
          const p = prev[k];
          const n = idx[k];
          if (!p) return idx;
          if (p.last_message_id !== n.last_message_id || p.unread_count !== n.unread_count) return idx;
        }
        return prev; // nothing changed — keep same reference, no re-render
      });
    } catch { /* ignore polling errors */ }
  }, []);

  useEffect(() => {
    refreshThreads();
    threadsPollRef.current = setInterval(refreshThreads, 5000);
    return () => {
      if (threadsPollRef.current) clearInterval(threadsPollRef.current);
    };
  }, [refreshThreads]);

  // Load real sidebar data from existing APIs — scoped to the current academic year
  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    Promise.all([
      studentsAPI.getAll({ year }).catch(() => ({ data: { data: [] } })),
      usersAPI.getAll().catch(() => ({ data: { data: [] } })),
      studentsAPI.getClasses(year).catch(() => ({ data: { data: [] } })),
    ])
      .then(([sRes, uRes, cRes]) => {
        if (cancelled) return;
        const s = sRes.data?.data || sRes.data || [];
        const u = uRes.data?.data || uRes.data || [];
        const c = cRes.data?.data || cRes.data || [];
        setStudents(Array.isArray(s) ? s : []);
        setTeachers(Array.isArray(u) ? u : []);
        setClasses(Array.isArray(c) ? c : []);
      })
      .catch((err) => { if (!cancelled) setLoadErr(err.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  useEffect(() => {
    setPickedClass('');
    setActiveId(null);
  }, [category]);

  // Load custom (school-saved) message templates once on mount. Merged with
  // the built-in starter set in the picker. Non-fatal if it fails.
  useEffect(() => {
    messageTemplatesAPI.getAll()
      .then((r) => setCustomTemplates(r.data?.data || []))
      .catch(() => { /* ignore */ });
  }, []);

  // Build sidebar list from REAL data
  const listItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const match = (text) => !term || (text || '').toString().toLowerCase().includes(term);

    const studentToItem = (s) => ({
      id: `stu-${s.student_id}`,
      name: s.student_name,
      avatar: initialsOf(s.student_name),
      subtitle: `Class ${s.class_name || '-'}${s.father_name ? ' · ' + s.father_name : ''}`,
    });

    const teacherToItem = (u) => ({
      id: `usr-${u.user_id}`,
      username: u.username,
      name: u.full_name || u.username,
      avatar: initialsOf(u.full_name || u.username),
      subtitle: u.username ? `@${u.username}` : 'Staff',
    });

    const parentToItem = (s) => ({
      id: `par-${s.student_id}`,
      name: `${s.father_name || 'Parent'}`,
      avatar: initialsOf(s.father_name || 'P'),
      subtitle: `Parent of ${s.student_name}${s.mobile_number ? ' · ' + s.mobile_number : ''}`,
    });

    // Convert a backend thread row to a sidebar item (for the "All" view).
    const threadToItem = (tk, meta) => {
      // dm:<a>|<b> → label = other party
      if (tk.startsWith('dm:')) {
        const parts = tk.slice(3).split('|');
        const other = parts.find((p) => p.toLowerCase() !== (meUsername || '').toLowerCase()) || parts[0];
        const t = teachers.find((u) => (u.username || '').toLowerCase() === other.toLowerCase());
        const name = t ? (t.full_name || t.username) : other;
        return { id: `usr-${t?.user_id || other}`, username: other, name, avatar: initialsOf(name) };
      }
      if (tk === 'bc:students') return { id: 'broadcast-students', name: 'All Students Broadcast', avatar: 'AS' };
      if (tk === 'bc:teachers') return { id: 'broadcast-teachers', name: 'All Teachers',           avatar: 'AT' };
      if (tk === 'bc:parents')  return { id: 'broadcast-parents',  name: 'All Parents Broadcast',  avatar: 'AP' };
      if (tk.startsWith('cls:')) {
        const isParents = tk.endsWith(':parents');
        const cls = tk.slice(4).replace(/:parents$/, '');
        const name = isParents ? `Parents of Class ${cls}` : `Class ${cls}`;
        const id   = isParents ? `cls-${cls}-par` : `cls-${cls}-grp`;
        return { id, name, avatar: isParents ? 'P' : cls.slice(0, 3).toUpperCase() };
      }
      if (tk.startsWith('stu:')) {
        const isParent = tk.endsWith(':parent');
        const idStr = tk.slice(4).replace(/:parent$/, '');
        const s = students.find((x) => String(x.student_id) === idStr);
        const name = s ? (isParent ? (s.father_name || `Parent of ${s.student_name}`) : s.student_name)
                       : meta.thread_title || 'Conversation';
        return {
          id: isParent ? `par-${idStr}` : `stu-${idStr}`,
          name,
          avatar: initialsOf(name),
        };
      }
      return { id: `tk-${tk}`, name: meta.thread_title || tk, avatar: '?' };
    };

    // Attach unread badge, last-message preview, and a sort timestamp.
    const decorate = (items) => items.map((it) => {
      const t = resolveThread(it, meUsername);
      const tk = t?.thread_key || '';
      const pinned = tk && pins.includes(tk);
      if (!t) return { ...it, unread: 0, _last_at: 0, _pinned: false, _tk: '' };
      const meta = threadIdx[tk];
      if (!meta) return { ...it, unread: 0, _last_at: 0, _pinned: pinned, _tk: tk };
      const decoded = meta.last_message ? decodePayload(meta.last_message) : { body: '' };
      const previewBody = decoded.body || (decoded.meta?.file ? `\u{1F4CE} ${decoded.meta.file.name}` : '');
      const preview = previewBody
        ? (meta.sender_name ? `${meta.sender_name}: ` : '') + previewBody
        : it.subtitle;
      return {
        ...it,
        subtitle: preview && preview.length > 60 ? preview.slice(0, 57) + '\u2026' : preview,
        unread:   meta.unread_count || 0,
        _last_at: meta.created_at ? new Date(meta.created_at).getTime() : 0,
        _pinned:  pinned,
        _tk:      tk,
      };
    });

    // Sort: pinned first (by recency), then items with a recent message,
    // then the rest in their existing order.
    const sortByActivity = (items) => {
      const pinnedItems = items.filter((i) => i._pinned).sort((a, b) => b._last_at - a._last_at);
      const rest        = items.filter((i) => !i._pinned);
      const withChat = rest.filter((i) => i._last_at > 0).sort((a, b) => b._last_at - a._last_at);
      const without  = rest.filter((i) => !(i._last_at > 0));
      return [...pinnedItems, ...withChat, ...without];
    };

    if (category === 'individual') {
      return sortByActivity(decorate(
        students
          .filter((s) => match(s.student_name) || match(s.class_name) || match(s.father_name))
          .map(studentToItem)
      ));
    }

    if (category === 'class') {
      if (!pickedClass) return [];
      const classKids = students.filter((s) => String(s.class_name) === String(pickedClass));
      const base = [
        { id: `cls-${pickedClass}-grp`, name: `Class ${pickedClass} (Group)`,    avatar: String(pickedClass).slice(0, 3).toUpperCase(), subtitle: `${classKids.length} students` },
        { id: `cls-${pickedClass}-par`, name: `Parents of Class ${pickedClass}`, avatar: 'P', subtitle: `${classKids.filter((k) => k.mobile_number).length} parents with mobile` },
        ...classKids.map(studentToItem),
      ].filter((c) => match(c.name) || match(c.subtitle));
      return sortByActivity(decorate(base));
    }

    if (category === 'students') {
      return sortByActivity(decorate([
        { id: 'broadcast-students', name: 'All Students Broadcast', avatar: 'AS', subtitle: `${students.length} students` },
        ...students.filter((s) => match(s.student_name)).map(studentToItem),
      ]));
    }

    if (category === 'teachers') {
      return sortByActivity(decorate([
        { id: 'broadcast-teachers', name: 'All Teachers', avatar: 'AT', subtitle: `${teachers.length} staff` },
        ...teachers.filter((u) => match(u.full_name) || match(u.username)).map(teacherToItem),
      ]));
    }

    if (category === 'parents') {
      const withParent = students.filter((s) => s.father_name || s.mobile_number);
      return sortByActivity(decorate([
        { id: 'broadcast-parents', name: 'All Parents Broadcast', avatar: 'AP', subtitle: `${withParent.length} parents` },
        ...withParent.filter((s) => match(s.father_name) || match(s.student_name) || match(s.mobile_number)).map(parentToItem),
      ]));
    }

    if (category === 'transport' || category === 'accounts') {
      return [];
    }

    // ── "All Conversations": show real threads ordered by recency ──
    const threadKeys = Object.keys(threadIdx);
    if (threadKeys.length > 0) {
      return decorate(
        threadKeys
          .map((tk) => ({ tk, meta: threadIdx[tk] }))
          .sort((a, b) => new Date(b.meta.created_at) - new Date(a.meta.created_at))
          .map(({ tk, meta }) => ({ ...threadToItem(tk, meta), subtitle: meta.last_message }))
      ).filter((c) => match(c.name) || match(c.subtitle));
    }
    // Fallback when no conversations yet: show entry points to start one.
    return decorate([
      { id: 'broadcast-students', name: 'All Students Broadcast', avatar: 'AS', subtitle: `${students.length} students` },
      { id: 'broadcast-teachers', name: 'All Teachers',           avatar: 'AT', subtitle: `${teachers.length} staff` },
      { id: 'broadcast-parents',  name: 'All Parents Broadcast',  avatar: 'AP', subtitle: `${students.filter((s) => s.father_name || s.mobile_number).length} parents` },
    ]).filter((c) => match(c.name) || match(c.subtitle));
  }, [category, pickedClass, search, students, teachers, threadIdx, meUsername, pins]);

  const active = useMemo(
    () => listItems.find((c) => c.id === activeId) || null,
    [listItems, activeId]
  );

  const thread = useMemo(() => resolveThread(active, meUsername), [active, meUsername]);

  const pollNew = useCallback(async (tk) => {
    if (!tk) return;
    try {
      const res = await chatAPI.getMessages(tk, lastIdRef.current);
      const list = res.data?.data || [];
      if (list.length > 0) {
        setMessages((prev) => [...prev, ...list]);
        lastIdRef.current = list[list.length - 1].message_id;
        // Beep + browser notification for incoming (not mine)
        const meLc = (meUsername || '').toLowerCase();
        const incoming = list.filter((m) => (m.sender_username || '').toLowerCase() !== meLc);
        if (incoming.length > 0) {
          playBeep();
          if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
            const m = incoming[incoming.length - 1];
            const { body } = decodePayload(m.content);
            try {
              new Notification(m.sender_name || m.sender_username || 'New message', {
                body: (body || '').slice(0, 120) || 'New message',
                tag:  `chat-${tk}`,
              });
            } catch { /* ignore */ }
          }
        }
      }
    } catch { /* swallow polling errors */ }
  }, [meUsername]);

  // Ask for notification permission once on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
    }
  }, []);

  // Load + poll messages whenever the active thread changes
  useEffect(() => {
    setMessages([]);
    setMsgErr('');
    lastIdRef.current = 0;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!thread) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await chatAPI.getMessages(thread.thread_key, 0);
        if (cancelled) return;
        const list = res.data?.data || [];
        setMessages(list);
        lastIdRef.current = list.length ? list[list.length - 1].message_id : 0;
      } catch (err) {
        if (!cancelled) setMsgErr(err.response?.data?.message || err.message || 'Failed to load messages');
      }
    })();

    pollRef.current = setInterval(() => pollNew(thread.thread_key), 3000);

    // Read-status (✓✓) for DMs only
    setOtherSeenId(0);
    if (readStatusPollRef.current) { clearInterval(readStatusPollRef.current); readStatusPollRef.current = null; }
    if (thread.thread_key.startsWith('dm:')) {
      const refreshReadStatus = async () => {
        try {
          const r = await chatAPI.getReadStatus(thread.thread_key);
          if (!cancelled && r.data?.data) setOtherSeenId(r.data.data.last_seen_message_id || 0);
        } catch { /* ignore */ }
      };
      refreshReadStatus();
      readStatusPollRef.current = setInterval(refreshReadStatus, 5000);
    }

    // Clear composer extras when switching threads
    setReplyTo(null);
    setAttachment(null);
    setShowEmoji(false);
    setConvoSearch('');

    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (readStatusPollRef.current) { clearInterval(readStatusPollRef.current); readStatusPollRef.current = null; }
    };
  }, [thread, pollNew]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Mark the active thread as read whenever new messages arrive or thread opens.
  useEffect(() => {
    if (!thread || messages.length === 0) return;
    const lastId = messages[messages.length - 1].message_id;
    chatAPI.markRead(thread.thread_key, lastId)
      .then(() => {
        // Optimistically zero unread in local index so badge clears immediately
        setThreadIdx((prev) => {
          const cur = prev[thread.thread_key];
          if (!cur || cur.unread_count === 0) return prev;
          return { ...prev, [thread.thread_key]: { ...cur, unread_count: 0 } };
        });
      })
      .catch(() => { /* silent */ });
  }, [thread, messages]);

  const sendMessage = async () => {
    const text = draft.trim();
    if ((!text && !attachment) || !thread || sending) return;
    const meta = (replyTo || attachment) ? {
      reply: replyTo ? { id: replyTo.id, author: replyTo.author, text: replyTo.text } : undefined,
      file:  attachment || undefined,
    } : null;
    const payload = encodePayload(meta, text);
    setSending(true);
    try {
      const res = await chatAPI.send(thread.thread_key, payload, thread.thread_title);
      const m = res.data?.data;
      if (m) {
        setMessages((prev) => [...prev, m]);
        lastIdRef.current = m.message_id;
      }
      setDraft('');
      setReplyTo(null);
      setAttachment(null);
      setShowEmoji(false);
      setMsgErr('');
    } catch (err) {
      setMsgErr(err.response?.data?.message || err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const insertEmoji = (e) => {
    setDraft((d) => d + e);
    if (draftInputRef.current) draftInputRef.current.focus();
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setMsgErr(`File too large. Max ${Math.round(MAX_FILE_BYTES / 1024)} KB.`);
      return;
    }
    try {
      const data = await readFileAsDataURL(f);
      setAttachment({ name: f.name, type: f.type || 'application/octet-stream', data });
      setMsgErr('');
    } catch {
      setMsgErr('Could not read file');
    }
  };

  const togglePin = (thread_key) => {
    setPins((prev) => {
      const next = prev.includes(thread_key)
        ? prev.filter((k) => k !== thread_key)
        : [...prev, thread_key];
      savePins(next);
      return next;
    });
  };

  const startReplyTo = (m) => {
    const { body } = decodePayload(m.content);
    setReplyTo({
      id:     m.message_id,
      author: m.sender_name || m.sender_username || 'User',
      text:   (body || '').slice(0, 140),
    });
    if (draftInputRef.current) draftInputRef.current.focus();
  };

  // True for threads whose recipients have phone numbers (broadcast to
  // students/parents, class groups, individual students). False for staff
  // DMs and the all-teachers broadcast.
  const canWhatsapp = useMemo(() => {
    if (!thread) return false;
    const tk = thread.thread_key;
    return tk === 'bc:students' || tk === 'bc:parents'
        || tk.startsWith('cls:') || tk.startsWith('stu:');
  }, [thread]);

  const sendWhatsapp = async () => {
    const text = draft.trim();
    if (!text || !thread || waSending || !canWhatsapp) return;
    const ok = window.confirm(
      `Send this message to recipients of "${thread.thread_title}" via WhatsApp?\n\n` +
      `It will also be saved in the chat history.`
    );
    if (!ok) return;
    setWaSending(true);
    try {
      const res = await chatAPI.sendWhatsapp(thread.thread_key, text, thread.thread_title);
      const m = res.data?.data;
      if (m) {
        setMessages((prev) => [...prev, m]);
        lastIdRef.current = m.message_id;
      }
      setDraft('');
      setMsgErr('');
      const { sent = 0, failed = 0, total = 0 } = res.data || {};
      window.alert(`WhatsApp delivery: ${sent}/${total} sent${failed ? `, ${failed} failed` : ''}`);
    } catch (err) {
      setMsgErr(err.response?.data?.message || err.message || 'WhatsApp send failed');
    } finally {
      setWaSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const categoryHint = useMemo(() => {
    switch (category) {
      case 'students':   return `Showing ${students.length} students from your school`;
      case 'teachers':   return `Showing ${teachers.length} staff members`;
      case 'parents':    return `Showing parents derived from student records`;
      case 'transport':  return 'Transport staff module not configured yet';
      case 'accounts':   return 'Accounts department module not configured yet';
      case 'class':      return pickedClass ? `Showing Class ${pickedClass}` : 'Pick a class to continue';
      case 'individual': return 'Pick a student to start a 1:1 chat';
      default:           return loading ? '' : 'Pick a category to filter';
    }
  }, [category, pickedClass, students, teachers, loading]);

  return (
    <div style={{ padding: 16 }}>
      <div style={styles.shell}>
        {/* ── Left Sidebar ───────────────────────────────────────────── */}
        <aside style={styles.sidebar}>
          <div style={styles.sbHeader}>
            <span style={styles.sbTitle}>💬 Messages</span>
            <button style={styles.newBtn} onClick={() => setCategory('teachers')}>
              ＋ New
            </button>
          </div>

          <div style={styles.sbToolbar}>
            <input
              style={styles.search}
              type="text"
              placeholder="🔍 Search chats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              style={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            {category === 'class' && (
              <select
                style={styles.select}
                value={pickedClass}
                onChange={(e) => setPickedClass(e.target.value)}
              >
                <option value="">— Choose class —</option>
                {classes.map((c) => {
                  const label = typeof c === 'string' ? c : (c.class_name || c.name || '');
                  return label ? <option key={label} value={label}>Class {label}</option> : null;
                })}
              </select>
            )}
          </div>

          {categoryHint && <div style={styles.catNote}>{categoryHint}</div>}

          <div style={styles.list}>
            {loading ? (
              <div style={styles.loading}>Loading data from your school…</div>
            ) : loadErr ? (
              <div style={styles.empty}>Couldn't load data: {loadErr}</div>
            ) : listItems.length === 0 ? (
              <div style={styles.empty}>
                {category === 'class' && !pickedClass
                  ? 'Select a class from the dropdown above.'
                  : (category === 'transport' || category === 'accounts')
                    ? 'No data available for this category yet.'
                    : 'No matches.'}
              </div>
            ) : listItems.map((c) => (
              <ConversationItem
                key={c.id}
                convo={c}
                active={c.id === activeId}
                onClick={() => setActiveId(c.id)}
                onPin={togglePin}
              />
            ))}
          </div>
        </aside>

        {/* ── Chat Pane ──────────────────────────────────────────────── */}
        <section style={styles.chatPane}>
          {!active ? (
            <div style={styles.placeholder}>
              <div style={styles.phIcon}>💬</div>
              <h3 style={{ margin: '0 0 4px', color: '#374151' }}>Internal Communication</h3>
              <p style={{ margin: 0, fontSize: 13 }}>
                Pick a category on the left, then select a conversation to start messaging.
              </p>
              <button
                type="button"
                onClick={() => setShowTemplates(true)}
                style={{
                  marginTop: 16,
                  background: '#25d366',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 20,
                  padding: '10px 20px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37,211,102,0.35)',
                }}
                title="Browse ready-made message templates (holidays, exams, fees, etc.)"
              >
                📋 Browse Message Templates
              </button>
            </div>
          ) : (
            <>
              <header style={styles.chatHead}>
                <Avatar initials={active.avatar} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.chatTitle}>{active.name}</div>
                  <div style={styles.chatSub}>
                    {thread ? active.subtitle : 'Not messageable from your account'}
                  </div>
                </div>
              </header>

              {msgErr && <div style={styles.err}>{msgErr}</div>}

              <div style={{ padding: '6px 14px', background: '#f0f2f5', borderBottom: '1px solid #e5e7eb' }}>
                <input
                  type="text"
                  placeholder="🔎 Search in this conversation..."
                  value={convoSearch}
                  onChange={(e) => setConvoSearch(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 16, border: '1px solid #d1d5db', fontSize: 12, outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                />
              </div>
              <div style={styles.msgArea} ref={scrollRef}>
                {!thread ? (
                  <div style={{ ...styles.placeholder, background: 'transparent' }}>
                    <p style={{ fontSize: 13 }}>
                      This recipient can't receive in-app messages from your account.
                    </p>
                  </div>
                ) : messages.length === 0 ? (
                  <div style={{ ...styles.placeholder, background: 'transparent' }}>
                    <p style={{ fontSize: 13 }}>No messages yet — say hello 👋</p>
                  </div>
                ) : (() => {
                  const meLc  = (meUsername || '').toLowerCase();
                  const term  = convoSearch.trim().toLowerCase();
                  let prevDay = '';
                  const nodes = [];
                  messages.forEach((m) => {
                    const decoded = decodePayload(m.content);
                    const body    = decoded.body || '';
                    const meta    = decoded.meta || null;
                    if (term) {
                      const hay = `${m.sender_name || ''} ${m.sender_username || ''} ${body}`.toLowerCase();
                      if (!hay.includes(term)) return;
                    }
                    const day = new Date(m.created_at).toDateString();
                    if (day !== prevDay) {
                      prevDay = day;
                      nodes.push(
                        <div key={`sep-${m.message_id}`} style={{ alignSelf: 'center', background: '#ffffffcc', color: '#374151', fontSize: 11, padding: '3px 10px', borderRadius: 12, margin: '6px 0', boxShadow: '0 1px 1px rgba(0,0,0,0.05)' }}>
                          {fmtDateSep(m.created_at)}
                        </div>
                      );
                    }
                    const mine = (m.sender_username || '').toLowerCase() === meLc;
                    let ticks = null;
                    if (mine && thread && thread.thread_key.startsWith('dm:')) {
                      ticks = (
                        <span style={{ marginLeft: 4, color: m.message_id <= otherSeenId ? '#34b7f1' : '#6b7280' }}>
                          {m.message_id <= otherSeenId ? '✓✓' : '✓'}
                        </span>
                      );
                    } else if (mine) {
                      ticks = <span style={{ marginLeft: 4, color: '#6b7280' }}>✓</span>;
                    }
                    nodes.push(
                      <div key={m.message_id} style={{ ...styles.bubbleRow, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                        <div
                          style={{ ...styles.bubble, ...(mine ? styles.bubbleMine : styles.bubbleThem), cursor: 'pointer' }}
                          onDoubleClick={() => startReplyTo(m)}
                          title="Double-click to reply"
                        >
                          {!mine && (
                            <div style={styles.bubbleAuthor}>{m.sender_name || m.sender_username}</div>
                          )}
                          {meta?.reply && (
                            <div style={{ borderLeft: '3px solid #075e54', padding: '4px 8px', margin: '0 0 6px', background: 'rgba(7,94,84,0.08)', borderRadius: 4, fontSize: 12 }}>
                              <div style={{ fontWeight: 600, color: '#075e54' }}>{meta.reply.author}</div>
                              <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{meta.reply.text}</div>
                            </div>
                          )}
                          {meta?.file && (
                            <div style={{ margin: '0 0 6px' }}>
                              {meta.file.type?.startsWith('image/') ? (
                                <a href={meta.file.data} target="_blank" rel="noreferrer">
                                  <img
                                    src={meta.file.data}
                                    alt={meta.file.name}
                                    style={{ maxWidth: 260, maxHeight: 220, borderRadius: 6, display: 'block' }}
                                  />
                                </a>
                              ) : (
                                <a href={meta.file.data} download={meta.file.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#f1f5f9', borderRadius: 6, color: '#075e54', textDecoration: 'none', fontSize: 13 }}>
                                  📎 {meta.file.name}
                                </a>
                              )}
                            </div>
                          )}
                          {body && <div>{body}</div>}
                          <div style={styles.bubbleMeta}>
                            {fmtTime(m.created_at)}{ticks}
                          </div>
                        </div>
                      </div>
                    );
                  });
                  return nodes;
                })()}
              </div>

              {(replyTo || attachment) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#e7f3ff', borderTop: '1px solid #cbd5e1' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {replyTo && (
                      <div style={{ borderLeft: '3px solid #075e54', paddingLeft: 8, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: '#075e54' }}>Replying to {replyTo.author}</div>
                        <div style={{ color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyTo.text}</div>
                      </div>
                    )}
                    {attachment && (
                      <div style={{ marginTop: replyTo ? 6 : 0, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {attachment.type?.startsWith('image/')
                          ? <img src={attachment.data} alt="" style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover' }} />
                          : <span>📎</span>}
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{attachment.name}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setReplyTo(null); setAttachment(null); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}
                    title="Cancel"
                  >✕</button>
                </div>
              )}

              {showEmoji && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4, padding: 10, background: '#fff', borderTop: '1px solid #e5e7eb' }}>
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      onClick={() => insertEmoji(e)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, padding: 4 }}
                    >{e}</button>
                  ))}
                </div>
              )}

              <div style={styles.composer}>
                <button
                  type="button"
                  onClick={() => setShowEmoji((v) => !v)}
                  disabled={!thread}
                  title="Emoji"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, padding: '0 4px', opacity: thread ? 1 : 0.4 }}
                >😀</button>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={onPickFile}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={!thread}
                  title="Attach file (max 500 KB)"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, padding: '0 4px', opacity: thread ? 1 : 0.4 }}
                >📎</button>
                <button
                  type="button"
                  onClick={() => setShowTemplates(true)}
                  title="Insert from message templates"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, padding: '0 4px' }}
                >📋</button>
                <input
                  ref={draftInputRef}
                  style={styles.input}
                  type="text"
                  placeholder={thread ? 'Type a message...' : 'Select a messageable recipient'}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={!thread || sending || waSending}
                />
                {canWhatsapp && !attachment && (
                  <button
                    style={{
                      background: '#25d366', color: '#fff', border: 'none',
                      borderRadius: '50%', width: 42, height: 42, cursor: 'pointer',
                      fontSize: 18, display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                      opacity: !draft.trim() || waSending ? 0.5 : 1,
                    }}
                    onClick={sendWhatsapp}
                    disabled={!draft.trim() || waSending}
                    title="Send via WhatsApp to recipients with mobile numbers"
                  >📱</button>
                )}
                <button
                  style={{ ...styles.sendBtn, opacity: (!thread || (!draft.trim() && !attachment) || sending) ? 0.5 : 1 }}
                  onClick={sendMessage}
                  disabled={!thread || (!draft.trim() && !attachment) || sending}
                  title="Send (in-app only)"
                >➤</button>
              </div>
            </>
          )}
        </section>
      </div>

      {showTemplates && (
        <TemplatePickerModal
          customTemplates={customTemplates}
          schoolName={(typeof window !== 'undefined' && sessionStorage.getItem('gs_school_name')) || 'School'}
          onClose={() => setShowTemplates(false)}
          onInsert={(text) => {
            setDraft((d) => (d ? `${d}\n${text}` : text));
            setShowTemplates(false);
            setTimeout(() => { if (draftInputRef.current) draftInputRef.current.focus(); }, 0);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Template Picker Modal
//
// Two-pane modal: left = category list, right = templates in the chosen
// category. Selecting a template reveals a small form for any required extra
// fields (date, reason, etc.), then "Insert" pastes the rendered body into
// the chat composer's draft input.
// ─────────────────────────────────────────────────────────────────────────────
// ── Timetable Row Builder (used inside TemplatePickerModal) ──────────────────
const EMPTY_TT_ROW = () => ({ date: '', subject: '', syllabus: '' });

function TimetableRowsField({ value, onChange }) {
  const parseRows = (val) => {
    if (!val) return [EMPTY_TT_ROW()];
    const lines = val.split('\n').filter(Boolean);
    if (!lines.length) return [EMPTY_TT_ROW()];
    return lines.map((line) => {
      const parts = line.split(' | ');
      return { date: parts[0] || '', subject: parts[1] || '', syllabus: parts[2] || '' };
    });
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return d && m && y ? `${d}/${m}/${y}` : iso;
  };

  const toIso = (dd_mm_yyyy) => {
    if (!dd_mm_yyyy) return '';
    if (dd_mm_yyyy.includes('-')) return dd_mm_yyyy;
    const [d, m, y] = dd_mm_yyyy.split('/');
    return d && m && y ? `${y}-${m}-${d}` : '';
  };

  const [rows, setRows] = React.useState(() =>
    parseRows(value).map((r) => ({ ...r, date: toIso(r.date) }))
  );

  const commit = (updated) => {
    setRows(updated);
    const str = updated
      .filter((r) => r.date || r.subject || r.syllabus)
      .map((r) => [fmtDate(r.date) || r.date, r.subject, r.syllabus].filter(Boolean).join(' | '))
      .join('\n');
    onChange(str);
  };

  const update = (idx, field, val) =>
    commit(rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  const addRow    = () => commit([...rows, EMPTY_TT_ROW()]);
  const removeRow = (idx) => { const u = rows.filter((_, i) => i !== idx); commit(u.length ? u : [EMPTY_TT_ROW()]); };

  const cell  = { padding: '5px 7px', border: '1.5px solid #d1d5db', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', background: '#fff', minWidth: 0 };
  const hdr   = { padding: '5px 7px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' };

  return (
    <div style={{ border: '1.5px solid #d1d5db', borderRadius: 8, overflow: 'hidden', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 100px 1fr 28px', background: '#1e3a5f', gap: 1 }}>
        <div style={{ ...hdr, color: '#fff' }}>📅 Date</div>
        <div style={{ ...hdr, color: '#fff' }}>Subject</div>
        <div style={{ ...hdr, color: '#fff' }}>Syllabus / Topic</div>
        <div />
      </div>

      {/* Rows */}
      {rows.map((row, idx) => (
        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '120px 100px 1fr 28px', gap: 1, background: idx % 2 === 0 ? '#fff' : '#f8faff', borderTop: '1px solid #e5e7eb', alignItems: 'center', padding: '3px 4px' }}>
          <input type="date" style={cell} value={row.date}
            onChange={(e) => update(idx, 'date', e.target.value)} />
          <input style={cell} placeholder="ENGLISH" value={row.subject}
            onChange={(e) => update(idx, 'subject', e.target.value.toUpperCase())} />
          <input style={cell} placeholder="Topic / Chapter" value={row.syllabus}
            onChange={(e) => update(idx, 'syllabus', e.target.value)} />
          <button onClick={() => removeRow(idx)} type="button"
            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}
            title="Remove row">✕</button>
        </div>
      ))}

      {/* Add row */}
      <div style={{ padding: '6px 8px', borderTop: '1px dashed #d1d5db' }}>
        <button type="button" onClick={addRow}
          style={{ background: 'none', border: '1.5px dashed #22c55e', color: '#16a34a', borderRadius: 6, padding: '3px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add Row
        </button>
      </div>
    </div>
  );
}

function TemplatePickerModal({ customTemplates, schoolName, onClose, onInsert }) {
  const allTemplates = useMemo(
    () => [...BUILT_IN_TEMPLATES, ...customTemplates.map(adaptCustomTemplate)],
    [customTemplates],
  );

  // Show only categories that actually have at least one template.
  const visibleCategories = useMemo(() => {
    const counts = allTemplates.reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + 1;
      return acc;
    }, {});
    return TEMPLATE_CATEGORIES.filter((c) => counts[c.key]);
  }, [allTemplates]);

  const [activeCat, setActiveCat] = useState(visibleCategories[0]?.key || 'announcement');
  const [selected,  setSelected]  = useState(null);
  const [extra,     setExtra]     = useState({});

  const visibleTemplates = allTemplates.filter((t) => t.category === activeCat);
  const previewText      = selected ? renderTemplateBody(selected, extra, schoolName) : '';

  const requiredFilled = !selected
    ? false
    : selected.extraFields.every((f) => !!(extra[f.key] || '').trim());

  const handleSelect = (t) => {
    setSelected(t);
    setExtra({});
  };

  const handleInsert = () => {
    if (!selected) return;
    if (selected.extraFields.length > 0 && !requiredFilled) return;
    onInsert(previewText);
  };

  // Styles kept inline to match the rest of this file and avoid leaking CSS.
  const m = {
    backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 },
    panel:    { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 920, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' },
    header:   { padding: '14px 18px', background: '#075e54', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    title:    { fontSize: 16, fontWeight: 600 },
    close:    { background: 'transparent', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1 },
    body:     { display: 'flex', flex: 1, minHeight: 360, overflow: 'hidden' },
    catCol:   { width: 200, borderRight: '1px solid #e5e7eb', background: '#f8fafc', overflowY: 'auto', flexShrink: 0 },
    catBtn:   { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '12px 14px', fontSize: 13.5, cursor: 'pointer', color: '#374151', borderBottom: '1px solid #f1f5f9' },
    catBtnActive: { background: '#e7f3ff', color: '#075e54', fontWeight: 600 },
    middle:   { width: 280, borderRight: '1px solid #e5e7eb', overflowY: 'auto', flexShrink: 0 },
    tmplBtn:  { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '12px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f1f5f9' },
    tmplBtnActive: { background: '#fffbeb' },
    tmplLabel: { fontWeight: 600, color: '#111827' },
    tmplDesc:  { fontSize: 11.5, color: '#6b7280', marginTop: 2 },
    right:    { flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 },
    field:    { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#374151' },
    input:    { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, outline: 'none' },
    textarea: { padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', resize: 'vertical', minHeight: 130, lineHeight: 1.6, background: '#fff', transition: 'border-color 0.15s' },
    preview:  { background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 12, fontSize: 13, whiteSpace: 'pre-wrap', color: '#111827', lineHeight: 1.45, maxHeight: 220, overflowY: 'auto' },
    perStudentNote: { background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: 6, padding: 8, fontSize: 11.5, color: '#1e40af' },
    footer:   { padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' },
    btnGrey:  { background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnGreen: { background: '#25d366', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    placeholder: { color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 30 },
  };

  return (
    <div style={m.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={m.panel}>
        <div style={m.header}>
          <span style={m.title}>📋 Message Templates</span>
          <button style={m.close} onClick={onClose} title="Close">×</button>
        </div>

        <div style={m.body}>
          {/* Categories */}
          <div style={m.catCol}>
            {visibleCategories.map((c) => (
              <button
                key={c.key}
                style={activeCat === c.key ? { ...m.catBtn, ...m.catBtnActive } : m.catBtn}
                onClick={() => { setActiveCat(c.key); setSelected(null); }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Templates in category */}
          <div style={m.middle}>
            {visibleTemplates.length === 0 ? (
              <div style={m.placeholder}>No templates in this category yet.</div>
            ) : visibleTemplates.map((t) => (
              <button
                key={t.id}
                style={selected?.id === t.id ? { ...m.tmplBtn, ...m.tmplBtnActive } : m.tmplBtn}
                onClick={() => handleSelect(t)}
              >
                <div style={m.tmplLabel}>{t.label}</div>
                <div style={m.tmplDesc}>{t.description}</div>
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <div style={m.right}>
            {!selected ? (
              <div style={m.placeholder}>Pick a template on the left to preview it.</div>
            ) : (
              <>
                {selected.extraFields.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {selected.extraFields.map((f) => (
                      <label key={f.key} style={m.field}>
                        <span style={{ fontWeight: 600 }}>{f.label} <span style={{ color: '#c62828' }}>*</span></span>
                        {f.type === 'timetable-rows' ? (
                          <TimetableRowsField
                            value={extra[f.key] || ''}
                            onChange={(val) => setExtra((p) => ({ ...p, [f.key]: val }))}
                          />
                        ) : f.type === 'textarea' ? (
                          <textarea
                            style={m.textarea}
                            rows={f.rows || 6}
                            placeholder={f.placeholder}
                            value={extra[f.key] || ''}
                            onChange={(e) => setExtra((p) => ({ ...p, [f.key]: e.target.value }))}
                          />
                        ) : (
                          <input
                            style={m.input}
                            type="text"
                            placeholder={f.placeholder}
                            value={extra[f.key] || ''}
                            onChange={(e) => setExtra((p) => ({ ...p, [f.key]: e.target.value }))}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                )}

                {selected.perStudent && (
                  <div style={m.perStudentNote}>
                    ℹ️ This template uses per-recipient placeholders like
                    <b> [Student Name]</b>, <b>[Father Name]</b>, <b>[Class]</b>, <b>[Amount]</b>.
                    They are filled per-parent automatically only when sent via
                    the WhatsApp Bulk page. In the chat, they will appear as
                    plain text — best used for individual student threads where
                    you can edit before sending.
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Preview</div>
                  <div style={m.preview}>{previewText}</div>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={m.footer}>
          <button style={m.btnGrey} onClick={onClose}>Cancel</button>
          <button
            style={{ ...m.btnGreen, opacity: !selected || (selected.extraFields.length > 0 && !requiredFilled) ? 0.5 : 1, cursor: !selected || (selected.extraFields.length > 0 && !requiredFilled) ? 'not-allowed' : 'pointer' }}
            onClick={handleInsert}
            disabled={!selected || (selected.extraFields.length > 0 && !requiredFilled)}
          >
            ✏️ Insert into message
          </button>
        </div>
      </div>
    </div>
  );
}
